/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import {
    HasProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import addProduct from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/AddProduct";
import removeProduct from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/RemoveProduct";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button"; // Store Custom Code
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import quickOrderSwapProduct from "@store/Handlers/QuickOrderSwapProduct";
import setSliderDrawerIsOpen from "@store/Handlers/SetSliderDrawerIsOpen";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    parentProduct: ProductContextModel;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    return {
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        addingProductToCart: state.context.addingProductToCart,
    };
};

const mapDispatchToProps = {
    setSliderDrawerIsOpen, // Store Custom Code
    removeProduct,
    addProduct,
    quickOrderSwapProduct,
};

export const quickOrderProductSwapButtonStyles: ButtonPresentationProps = {};

const QuickOrderProductSwapButton = ({
    productContext: {
        product: { id: productId },
        productInfo: { qtyOrdered, unitOfMeasure },
    },
    productContext,
    productSettings,
    hasEnoughInventory,
    addingProductToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    setSliderDrawerIsOpen, // Store Custom Code
    removeProduct,
    addProduct,
    parentProduct,
    quickOrderSwapProduct,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(quickOrderProductSwapButtonStyles, extendedStyles));

    if (!productSettings.canAddToCart || !hasEnoughInventory || !canAddToCart) {
        return null;
    }

    const swapProductInOrder = (
        productInfo: ProductInfo,
        product: ProductModel,
        parentProduct: ProductContextModel,
    ) => {
        quickOrderSwapProduct({ productInfo, product, parentProduct });
    };

    const swapClickHandler = () => {
        swapProductInOrder(productContext.productInfo, productContext.product, parentProduct);
        setSliderDrawerIsOpen({ sliderDrawerIsOpen: undefined });
    };

    return (
        <Button
            {...styles}
            onClick={swapClickHandler}
            disabled={qtyOrdered <= 0 || addingProductToCart}
            {...otherProps}
        >
            {labelOverride ?? translate("Swap product")}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(QuickOrderProductSwapButton));
